import { normalizeSeasonName } from "./playerFormatters";

function getStatValue(details, code) {
  const entry = details?.find((d) => d.type?.code === code);
  return entry ? entry.value : null;
}

export function summarizeSeason(seasonEntry) {
  const details = seasonEntry?.details || [];
  const season = seasonEntry?.season || {};

  const minutes = getStatValue(details, "minutes-played")?.total ?? 0;
  const appearances = getStatValue(details, "appearances")?.total ?? 0;
  const goals = getStatValue(details, "goals")?.total ?? 0;
  const assists = getStatValue(details, "assists")?.total ?? 0;
  const keyPasses = getStatValue(details, "key-passes")?.total ?? 0;
  const successfulDribbles = getStatValue(details, "successful-dribbles")?.total ?? 0;
  const totalDuels = getStatValue(details, "total-duels")?.total ?? 0;
  const duelsWon = getStatValue(details, "duels-won")?.total ?? 0;
  const tackles = getStatValue(details, "tackles")?.total ?? 0;
  const interceptions = getStatValue(details, "interceptions")?.total ?? 0;
  const passAccuracy = getStatValue(details, "accurate-passes-percentage")?.total ?? null;
  const shotsOnTarget = getStatValue(details, "shots-on-target")?.total ?? 0;
  const shotsTotal = getStatValue(details, "shots-total")?.total ?? 0;
  const bigChancesCreated = getStatValue(details, "big-chances-created")?.total ?? 0;
  const ratingObj = getStatValue(details, "rating");

  const per90 = (total) => (minutes > 0 ? (total / minutes) * 90 : 0);

  return {
    raw: seasonEntry,
    seasonId: season.id,
    seasonName: normalizeSeasonName(season.name) || "Unknown season",
    isCurrent: !!season.is_current,
    finished: !!season.finished,
    minutes,
    appearances,
    goals,
    assists,
    keyPasses,
    successfulDribbles,
    totalDuels,
    duelsWon,
    tackles,
    interceptions,
    passAccuracy,
    shotsOnTarget,
    shotsTotal,
    bigChancesCreated,
    rating: ratingObj?.average ?? null,
    goalsPer90: per90(goals),
    assistsPer90: per90(assists),
    minutesPerGoal: goals > 0 ? Math.round(minutes / goals) : null,
  };
}

function clampScore(v) {
  return Math.max(0, Math.min(10, v));
}

export function buildRadar(seasonSummary) {
  const s = seasonSummary;
  const per90 = (total) => (s.minutes > 0 ? (total / s.minutes) * 90 : 0);

  const chanceCreationPer90 = per90(s.assists + s.keyPasses + (s.bigChancesCreated ?? 0));
  const dribblesPer90 = per90(s.successfulDribbles);
  const defWorkPer90 = per90(s.tackles + s.interceptions);
  const duelSuccessPct = s.totalDuels > 0 ? (s.duelsWon / s.totalDuels) * 100 : 0;
  const aerialWonPer90 = per90(s.aerialsWon ?? 0);
  const shotAccuracyPct = s.shotsTotal > 0 ? (s.shotsOnTarget / s.shotsTotal) * 100 : 0;
  const involvement = Math.min(10, ((s.minutes ?? 0) / 1800) * 10);

  return [
    { key: "finishing", label: "Finishing", value: clampScore((s.goalsPer90 * 8) + (shotAccuracyPct / 20)), detail: `${s.goalsPer90.toFixed(2)} goals / 90 mins` },
    { key: "chanceCreation", label: "Chance Creation", value: clampScore(chanceCreationPer90 * 2.1), detail: `${chanceCreationPer90.toFixed(2)} actions / 90 mins` },
    { key: "passing", label: "Passing", value: clampScore((s.passAccuracy ?? 0) / 10), detail: s.passAccuracy != null ? `${s.passAccuracy.toFixed(1)}% accurate` : "n/a" },
    { key: "ballControl", label: "Ball Control", value: clampScore((dribblesPer90 * 1.8) + ((s.passAccuracy ?? 0) / 25)), detail: `${dribblesPer90.toFixed(2)} successful dribbles / 90 mins` },
    { key: "dribbling", label: "Dribbling", value: clampScore(dribblesPer90 * 2.5), detail: `${dribblesPer90.toFixed(2)} dribbles / 90 mins` },
    { key: "defending", label: "Defending", value: clampScore(defWorkPer90 * 3.3), detail: `${defWorkPer90.toFixed(2)} T+I / 90 mins` },
    { key: "duels", label: "Duels", value: clampScore(duelSuccessPct / 10), detail: `${duelSuccessPct.toFixed(1)}% duels won` },
    { key: "aerial", label: "Aerial", value: clampScore(aerialWonPer90 * 2.5), detail: `${aerialWonPer90.toFixed(2)} aerials won / 90 mins` },
    { key: "involvement", label: "Involvement", value: clampScore(involvement), detail: `${s.minutes ?? 0} minutes played` },
  ];
}

export function calcAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  const diff = Date.now() - dob.getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
}

function totalFrom(details, codes) {
  for (const code of codes) {
    const value = getStatValue(details, code)?.total;
    if (value != null) return value;
  }
  return 0;
}

export function buildCapabilities(seasonEntry, summary = {}) {
  const details = seasonEntry?.details || [];
  const get = (code) => details.find((d) => d.type?.code === code)?.value ?? null;
  const total = (...codes) => totalFrom(details, codes);
  const minutes = summary.minutes ?? total("minutes-played");
  const per90 = (value) => (minutes > 0 ? (value / minutes) * 90 : 0);

  const goals = total("goals");
  const shotsTotal = total("shots-total");
  const shotsOnTarget = total("shots-on-target");
  const shotsOffTarget = total("shots-off-target");
  const shotsBlocked = total("shots-blocked");

  const assists = total("assists");
  const keyPasses = total("key-passes");
  const bigChancesCreated = total("big-chances-created");
  const bigChancesMissed = total("big-chances-missed");

  const passes = total("passes");
  const accuratePasses = total("accurate-passes");
  const passAccuracy = get("accurate-passes-percentage")?.total ?? null;
  const longBalls = total("long-balls");
  const longBallsWon = total("long-balls-won");
  const throughBalls = total("through-balls");
  const throughBallsWon = total("through-balls-won");

  const dribbleAttempts = total("dribble-attempts");
  const successfulDribbles = total("successful-dribbles");
  const dispossessed = total("dispossessed");
  const dribbledPast = total("dribbled-past");

  const tackles = total("tackles");
  const interceptions = total("interceptions");
  const clearances = total("clearances");
  const fouls = total("fouls");
  const foulsDrawn = total("fouls-drawn");

  const totalDuels = total("total-duels");
  const duelsWon = total("duels-won");

  const aerialsWon = total("aeriels-won", "aerials-won");
  const hasAerials = details.some((d) => d.type?.code === "aeriels-won" || d.type?.code === "aerials-won");

  const appearances = total("appearances");
  const lineups = total("lineups");
  const bench = total("bench");

  return {
    finishing: {
      goals,
      shotsTotal,
      shotsOnTarget,
      shotsOffTarget,
      shotsBlocked,
      onTargetNotGoal: Math.max(0, shotsOnTarget - goals),
      conversion: shotsTotal > 0 ? (goals / shotsTotal) * 100 : 0,
      accuracy: shotsTotal > 0 ? (shotsOnTarget / shotsTotal) * 100 : 0,
      goalsPer90: per90(goals),
    },
    chanceCreation: {
      assists,
      keyPasses,
      bigChancesCreated,
      bigChancesMissed,
      total: assists + keyPasses + bigChancesCreated,
      per90: per90(assists + keyPasses + bigChancesCreated),
    },
    passing: {
      passes,
      accuratePasses,
      passAccuracy,
      longBalls,
      longBallsWon,
      throughBalls,
      throughBallsWon,
      passesPer90: per90(passes),
    },
    ballControl: {
      dispossessed,
      dribbledPast,
      foulsDrawn,
      dispossessedPer90: per90(dispossessed),
      dribbledPastPer90: per90(dribbledPast),
      foulsDrawnPer90: per90(foulsDrawn),
    },
    dribbling: {
      attempts: dribbleAttempts,
      successful: successfulDribbles,
      successRate: dribbleAttempts > 0 ? (successfulDribbles / dribbleAttempts) * 100 : 0,
      per90: per90(successfulDribbles),
    },
    defending: {
      tackles,
      interceptions,
      clearances,
      fouls,
      dribbledPast,
      actions: tackles + interceptions + clearances,
      per90: per90(tackles + interceptions + clearances),
    },
    duels: {
      total: totalDuels,
      won: duelsWon,
      lost: Math.max(0, totalDuels - duelsWon),
      successRate: totalDuels > 0 ? (duelsWon / totalDuels) * 100 : 0,
      per90: per90(totalDuels),
    },
    aerial: {
      won: aerialsWon,
      per90: per90(aerialsWon),
      available: hasAerials,
    },
    involvement: {
      minutes,
      appearances,
      lineups,
      bench,
      minutesPerApp: appearances > 0 ? minutes / appearances : 0,
      startShare: appearances > 0 ? (lineups / appearances) * 100 : 0,
    },
  };
}

const PITCH_LENGTH = 105;
const PITCH_WIDTH = 68;

export const PITCH_THIRDS = [
  { key: "defensive", label: "Defensive", from: 0, to: 35 },
  { key: "middle", label: "Middle", from: 35, to: 70 },
  { key: "attacking", label: "Attacking", from: 70, to: 105 },
];

export const PITCH_CHANNELS = [
  { key: "left", label: "Left", from: 0, to: PITCH_WIDTH / 3 },
  { key: "centre", label: "Centre", from: PITCH_WIDTH / 3, to: (PITCH_WIDTH * 2) / 3 },
  { key: "right", label: "Right", from: (PITCH_WIDTH * 2) / 3, to: PITCH_WIDTH },
];

const LINE_LABELS = {
  1: "Goalkeeper line",
  2: "Defensive line",
  3: "Midfield line",
  4: "Forward line",
  5: "Striker line",
};

const LINE_DEPTH = { 1: 6, 2: 26, 3: 52.5, 4: 79, 5: 93 };
const CHANNEL_DEPTH = { left: 11.33, centre: 34, right: 56.67 };

const HEAT_SIGMA = 9;
const HEAT_COLUMNS = 21;
const HEAT_ROWS = 14;

function parseFormation(field) {
  const [line, slot] = String(field).split(":").map(Number);
  if (!Number.isFinite(line) || !Number.isFinite(slot)) return null;
  return { line, slot };
}

function thirdFromLine(line) {
  if (line <= 2) return "defensive";
  if (line === 3) return "middle";
  return "attacking";
}

export function sideFromCode(code) {
  if (!code) return null;
  if (/right/i.test(code)) return "right";
  if (/left/i.test(code)) return "left";
  return "centre";
}

function venueOf(lineup) {
  const location = lineup.fixture?.participants?.find((p) => p.id === lineup.team_id)?.meta?.location;
  return location === "home" || location === "away" ? location : null;
}

function opponentOf(lineup) {
  return lineup.fixture?.participants?.find((p) => p.id !== lineup.team_id)?.name || null;
}

function channelLabel(channel) {
  return channel.charAt(0).toUpperCase() + channel.slice(1);
}

function lineLabel(line) {
  return LINE_LABELS[line] || `Line ${line}`;
}

function zoneLabel(line, channel) {
  const third = PITCH_THIRDS.find((entry) => entry.key === thirdFromLine(line));
  return `${third.label} ${channelLabel(channel)}`;
}

export function apportion(total, weights) {
  const sum = weights.reduce((acc, weight) => acc + weight, 0);
  if (total <= 0 || sum <= 0) return weights.map(() => 0);
  const exact = weights.map((weight) => (weight / sum) * total);
  const parts = exact.map(Math.floor);
  let remainder = total - parts.reduce((acc, part) => acc + part, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction);
  for (let i = 0; i < order.length && remainder > 0; i += 1) {
    parts[order[i].index] += 1;
    remainder -= 1;
  }
  return parts;
}

export function buildInvolvementMap(lineups, positionCode, seasonSummary = {}) {
  const played = (lineups || []).filter((lineup) => lineup.type_id === 11 && lineup.formation_field);
  const roleSide = sideFromCode(positionCode);

  const lineSizes = {};
  played.forEach((lineup) => {
    const parsed = parseFormation(lineup.formation_field);
    if (!parsed) return;
    lineSizes[parsed.line] = Math.max(lineSizes[parsed.line] || 0, parsed.slot);
  });

  const clusters = new Map();
  let withVenue = 0;

  played.forEach((lineup) => {
    const parsed = parseFormation(lineup.formation_field);
    if (!parsed) return;
    const venue = venueOf(lineup);
    if (venue) withVenue += 1;

    let channel = "centre";
    if (roleSide !== "centre") {
      const lineSize = lineSizes[parsed.line] || parsed.slot;
      if (venue) {
        const fromRight = venue === "home" ? parsed.slot : lineSize + 1 - parsed.slot;
        if (fromRight <= 1) channel = "right";
        else if (fromRight >= lineSize) channel = "left";
      } else if (roleSide) {
        channel = roleSide;
      }
    }

    const key = `${parsed.line}-${channel}`;
    if (!clusters.has(key)) {
      clusters.set(key, {
        key,
        line: parsed.line,
        channel,
        label: `${lineLabel(parsed.line)} · ${channelLabel(channel)}`,
        lineLabel: lineLabel(parsed.line),
        zone: zoneLabel(parsed.line, channel),
        x: LINE_DEPTH[parsed.line] ?? LINE_DEPTH[5],
        y: CHANNEL_DEPTH[channel],
        count: 0,
        formations: new Map(),
        matches: [],
      });
    }
    const cluster = clusters.get(key);
    cluster.count += 1;
    const formationKey = `${lineup.formation_field} ${venue || "unknown"}`;
    cluster.formations.set(formationKey, (cluster.formations.get(formationKey) || 0) + 1);
    const opponent = opponentOf(lineup);
    if (opponent) cluster.matches.push({ opponent, date: lineup.fixture?.starting_at || "" });
  });

  const total = played.length;
  const ordered = [...clusters.values()].sort((a, b) => b.count - a.count);
  const goals = Number(seasonSummary.goals) || 0;
  const assists = Number(seasonSummary.assists) || 0;
  const minutes = Number(seasonSummary.minutes) || 0;
  const weights = ordered.map((cluster) => cluster.count);
  const goalsSplit = apportion(goals, weights);
  const assistsSplit = apportion(assists, weights);
  const minutesSplit = apportion(minutes, weights);

  const positions = ordered.map((cluster, index) => ({
    ...cluster,
    share: total > 0 ? cluster.count / total : 0,
    estimatedGoals: goalsSplit[index],
    estimatedAssists: assistsSplit[index],
    estimatedMinutes: minutesSplit[index],
    formations: [...cluster.formations.entries()]
      .map(([formationKey, count]) => {
        const [field, venue] = formationKey.split(" ");
        return { field, venue, count };
      })
      .sort((a, b) => b.count - a.count),
    recentMatches: cluster.matches
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 3),
  }));

  const cellWidth = PITCH_LENGTH / HEAT_COLUMNS;
  const cellHeight = PITCH_WIDTH / HEAT_ROWS;
  const values = Array.from({ length: HEAT_ROWS }, () => new Array(HEAT_COLUMNS).fill(0));
  const reach = HEAT_SIGMA * 3.2;

  positions.forEach((position) => {
    for (let row = 0; row < HEAT_ROWS; row += 1) {
      const cellY = (row + 0.5) * cellHeight;
      for (let column = 0; column < HEAT_COLUMNS; column += 1) {
        const cellX = (column + 0.5) * cellWidth;
        const dx = cellX - position.x;
        const dy = cellY - position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > reach) continue;
        values[row][column] += position.count * Math.exp(-(distance * distance) / (2 * HEAT_SIGMA * HEAT_SIGMA));
      }
    }
  });

  const maxCell = Math.max(...values.flat(), 1);
  const heat = {
    columns: HEAT_COLUMNS,
    rows: HEAT_ROWS,
    cellWidth,
    cellHeight,
    values: values.map((row) => row.map((value) => value / maxCell)),
  };

  const maxStarts = Math.max(...positions.map((position) => position.count), 1);
  const peakStarts = Math.max(...positions.map((position) => position.count), 0);
  const peakKeys =
    peakStarts > 0 ? positions.filter((position) => position.count === peakStarts).map((position) => position.key) : [];

  return {
    positions,
    heat,
    total,
    withVenue,
    maxStarts,
    peakKeys,
    peakPositions: positions.filter((position) => peakKeys.includes(position.key)),
    roleSide,
    seasonTotals: {
      goals,
      assists,
      minutes,
      appearances: Number(seasonSummary.appearances) || 0,
    },
  };
}

export function buildPlayerInfo(data) {
  const profile = data?.profile;
  const foot = profile?.metadata?.find((entry) => entry.type?.code === "preferred-foot")?.values;
  return {
    club: data?.currentTeam?.overview?.name ?? profile?.teams?.[0]?.team?.name ?? null,
    nationality: profile?.nationality?.name ?? profile?.country?.name ?? null,
    birthDate: profile?.date_of_birth ?? null,
    strongFoot: foot ? foot.charAt(0).toUpperCase() + foot.slice(1) : null,
    agencies: null,
    estimatedValue: null,
  };
}

export function buildSeasonStats(seasonEntry) {
  const details = seasonEntry?.details || [];
  const total = (...codes) => {
    for (const code of codes) {
      const value = getStatValue(details, code)?.total;
      if (value != null) return value;
    }
    return null;
  };
  return {
    minutes: total("minutes-played"),
    matches: total("appearances"),
    fromStart: total("lineups"),
    goals: total("goals"),
    assists: total("assists"),
    yellowCards: total("yellowcards", "yellow-cards"),
    redCards: total("redcards", "red-cards"),
  };
}

export function buildTraits(radarAxes = []) {
  if (!radarAxes.length) return { strengths: [], weaknesses: [] };
  const sorted = [...radarAxes].sort((a, b) => b.value - a.value);
  return {
    strengths: sorted.slice(0, 3),
    weaknesses: sorted.slice(-3).reverse(),
  };
}

export function leagueNameForSeason(data, seasonId) {
  const active = data?.currentTeam?.overview?.activeseasons?.find(
    (season) => season.id === seasonId || season.season_id === seasonId,
  );
  return active?.league?.name ?? null;
}

function joinList(items) {
  if (items.length <= 1) return items[0] || "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

export function buildPlayerNarrative({
  name,
  age,
  role,
  club,
  strongFoot,
  seasonName,
  leagueName,
  stats,
  strengths = [],
  weaknesses = [],
}) {
  const strengthLabels = strengths.map((axis) => axis.label);
  const weaknessLabels = weaknesses.map((axis) => axis.label);
  let lead = "";

  if (age != null && role && club) {
    lead = strengthLabels.length
      ? `Age ${age} ${role} at ${club}: Strong in ${joinList(strengthLabels)}.`
      : `Age ${age} ${role} at ${club}.`;
  }

  const body = [];
  const foot = strongFoot ? `${strongFoot.toLowerCase()}-footed ` : "";
  if (name && role && club) {
    body.push(`${name} is a ${foot}${role.toLowerCase()} currently playing for ${club}.`);
  }

  if (name && seasonName && stats?.minutes != null && stats?.matches != null) {
    const league = leagueName ? ` ${leagueName}` : "";
    const startText = stats.fromStart != null ? `, starting ${stats.fromStart} of them` : "";
    const goalText = stats.goals != null ? `, scoring ${stats.goals} goals` : "";
    body.push(
      `In the ${seasonName}${league} season, ${name} has played ${stats.minutes.toLocaleString()} minutes across ${stats.matches} matches${startText}${goalText}.`,
    );
  }

  if (strengthLabels.length && weaknessLabels.length) {
    body.push(
      `Strengths this season include ${joinList(strengthLabels)}, while weaknesses are ${joinList(weaknessLabels)}.`,
    );
  }

  return { lead, body: body.join(" ") };
}

function formatMoney(amount) {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);
}

function formatInsightDate(date) {
  if (!date) return "unknown date";
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(new Date(date));
}

function sortedTransferRecords(transfers) {
  return [...(transfers || [])]
    .filter((transfer) => transfer.date)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

function valuedTransferRecords(records) {
  return records.filter((transfer) => Number.isFinite(Number(transfer.amount)) && Number(transfer.amount) > 0);
}

export function buildRadarInsight(axes = []) {
  if (axes.length < 4) return "Not enough radar data is available for this season.";
  const sorted = [...axes].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, 2).map((axis) => `${axis.label} (${axis.value.toFixed(1)})`);
  const low = sorted.slice(-2).map((axis) => `${axis.label} (${axis.value.toFixed(1)})`);
  return `${joinList(top)} lead the profile, while ${joinList(low)} are the weakest axes. Scores are normalised 0–10 from this season's totals.`;
}

export function buildCapabilityInsight(key, data) {
  if (!data) return "";
  switch (key) {
    case "finishing":
      return `${data.goals} goals from ${data.shotsTotal} shots — ${data.conversion.toFixed(1)}% conversion and ${data.accuracy.toFixed(1)}% on target.`;
    case "chanceCreation":
      return `${data.total} chances created (${data.per90.toFixed(2)} per 90) — ${data.assists} assists, ${data.keyPasses} key passes and ${data.bigChancesCreated} big chances.`;
    case "passing":
      return data.passAccuracy != null
        ? `${data.passAccuracy.toFixed(1)}% pass accuracy across ${data.passes.toLocaleString()} passes (${data.passesPer90.toFixed(1)} per 90), with ${data.longBallsWon} long balls won.`
        : `${data.passes.toLocaleString()} passes attempted (${data.passesPer90.toFixed(1)} per 90); accuracy was not recorded.`;
    case "ballControl":
      return `Dispossessed ${data.dispossessedPer90.toFixed(2)} times per 90 and dribbled past ${data.dribbledPastPer90.toFixed(2)}; wins ${data.foulsDrawnPer90.toFixed(2)} fouls per 90.`;
    case "dribbling":
      return `${data.successful} of ${data.attempts} take-ons completed (${data.successRate.toFixed(1)}%), ${data.per90.toFixed(2)} per 90.`;
    case "defending":
      return `${data.tackles} tackles, ${data.interceptions} interceptions and ${data.clearances} clearances — ${data.per90.toFixed(2)} defensive actions per 90.`;
    case "duels":
      return `Won ${data.won} of ${data.total} duels (${data.successRate.toFixed(1)}%), ${data.per90.toFixed(1)} contested per 90.`;
    case "aerial":
      return data.available
        ? `Won ${data.won} aerial duels (${data.per90.toFixed(2)} per 90).`
        : "No aerial duels were recorded for this season.";
    case "involvement":
      return `${data.minutes.toLocaleString()} minutes across ${data.appearances} appearances (${data.minutesPerApp.toFixed(1)} per appearance), starting ${data.lineups} matches.`;
    default:
      return "";
  }
}

export function buildTransferInsight(transfers = []) {
  const records = sortedTransferRecords(transfers);
  if (!records.length) return "No transfer history was returned for this player.";
  const latest = records[records.length - 1];
  const move = `${latest.fromteam?.name || "Unknown origin"} to ${latest.toteam?.name || "Unknown destination"} (${formatInsightDate(latest.date)})`;
  const valued = valuedTransferRecords(records);
  if (!valued.length) {
    const count = records.length === 1 ? "One recorded move" : `${records.length} recorded moves`;
    return `${count} — latest: ${move}. No fee was logged, so no value trend is available.`;
  }
  const highest = Math.max(...valued.map((transfer) => Number(transfer.amount)));
  const current = Number(valued[valued.length - 1].amount);
  return `Latest recorded value ${formatMoney(current)} across ${valued.length} priced ${valued.length === 1 ? "move" : "moves"}, peaking at ${formatMoney(highest)}. Latest move: ${move}.`;
}

export function buildInvolvementInsight(involvement, roleLabel) {
  if (!involvement || !involvement.total) {
    return "No played-match formation records are available for this season.";
  }
  const main = involvement.positions[0];
  const share = Math.round(main.share * 100);
  const role = roleLabel ? `, listed as ${roleLabel}` : "";
  return `${main.count} of ${involvement.total} starts came from the ${main.label.toLowerCase()} (${share}%)${role}. Goals, assists and minutes are apportioned by each zone's share of starts — an estimate from lineups, not tracking data.`;
}

export function pickReportSeasonEntry(rawStats, seasonId, seasonName) {
  const list = (rawStats || []).filter((entry) => entry?.season);
  if (!list.length) return null;
  const byId = list.filter((entry) => entry.season.id === seasonId);
  const candidates = byId.length
    ? byId
    : list.filter((entry) => normalizeSeasonName(entry.season.name) === seasonName);
  const pool = candidates.length ? candidates : [];
  if (!pool.length) return null;
  return [...pool].sort((a, b) => {
    const minutes = (entry) => Number(getStatValue(entry.details, "minutes-played")?.total) || 0;
    return minutes(b) - minutes(a);
  })[0];
}

export function resolveSeasonFromQuery(rawStats, query) {
  const list = (rawStats || []).filter((entry) => entry?.season);
  if (!list.length) return null;
  const sorted = [...list].sort((a, b) =>
    (b.season.starting_at || "").localeCompare(a.season.starting_at || ""),
  );
  const q = String(query || "").toLowerCase();

  const explicit = q.match(/(?:^|\D)(\d{4})\s*[\/-]\s*(\d{2,4})(?:\D|$)/);
  if (explicit) {
    const year = explicit[1];
    const short = explicit[2].length === 4 ? explicit[2].slice(-2) : explicit[2];
    const target = `${year}/${short}`;
    const match = sorted.find((entry) => normalizeSeasonName(entry.season.name) === target);
    if (match) return match;
  }

  const year = q.match(/\b(20\d{2})\b/);
  if (year) {
    const match = sorted.find((entry) => String(normalizeSeasonName(entry.season.name) || "").includes(year[1]));
    if (match) return match;
  }

  if (/\b(last|previous|prior|past)\b/.test(q) && sorted.length > 1) return sorted[1];
  return sorted[0];
}
